import { Button, Card, Form, Input, Radio, Select, Space, Typography, message } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
    DateRangePicker as ZhDateRangePicker,
    Grid as ZhGrid,
    InputNumber as ZhInputNumber,
    Upload as ZhUpload,
    type UploadRequestOptions
} from "zhihao-ui-react";

import request from "@/api";
import { fileService } from "@/api/services/fileService";

export interface AdvertisementFormContent {
    fileId?: number;
    advertId?: number;
    linkType?: number;
    link?: string;
    activityCode?: string;
    sort?: number;
    [key: string]: unknown;
}

export interface AdvertisementFormValues {
    positionType?: number;
    position?: number;
    displayForm: number;
    timeInterval?: number;
    beginTime?: string;
    endTime?: string;
    content: AdvertisementFormContent[];
}

export type AdvertisementFormPayload = Omit<AdvertisementFormValues, "content"> & {
    content?: AdvertisementFormContent[];
};

export interface AdvertisementFormPageProps {
    id?: number;
    loadDetail?: (id: number) => Promise<AdvertisementFormValues & { id?: number }>;
    update?: (id: number, values: AdvertisementFormPayload) => Promise<unknown>;
    loadAdvertOptions?: (keyword: string) => Promise<AdvertisementSelectorOption[]>;
    loadCampaignOptions?: (keyword: string) => Promise<AdvertisementSelectorOption[]>;
}

export interface AdvertisementSelectorOption {
    label: string;
    value: string | number;
}

interface AdvertisementUploadedFile {
    id: number;
    path?: string;
}

/** 复用 Vue PC 广告位详情接口，保持编辑页的原始数据来源。 */
export async function loadAdvertisementDetail(id: number): Promise<AdvertisementFormValues & { id?: number }> {
    const response = await request.service.get<AdvertisementFormValues & { id?: number }>(
        `/dataService/admin/advertisement-position/${id}`
    );
    return response.data;
}

/** 复用 Vue PC 广告位更新接口，并将上传后的图片 ID 与访问地址回填到保存 payload。 */
export async function updateAdvertisement(id: number, values: AdvertisementFormPayload): Promise<unknown> {
    const response = await request.service.put(`/dataService/admin/advertisement-position/${id}`, values);
    return response.data;
}

/** 复用 Vue adImg 的上传契约，使用 FormData.file 获取文件 ID 和访问路径。 */
export async function uploadAdvertisementImage(file: Blob): Promise<AdvertisementUploadedFile> {
    const formData = new FormData();
    formData.append("file", file);
    const uploadedFile = await fileService.uploadFile(formData);
    return { id: Number(uploadedFile.id), path: uploadedFile.path };
}

/** 复用 Vue imgContent 的 PC 广告页远程选择接口。 */
export async function loadAdvertisementAdvertOptions(keyword: string): Promise<AdvertisementSelectorOption[]> {
    const response = await request.service.get<{
        list?: Array<{ advert?: { id?: number | string; title?: string } }>;
    }>("/dataService/admin/advert/paging", { params: { key: keyword, classify: 1, page: 1, size: 50 } });
    return (response.data.list || [])
        .filter(item => item.advert?.id != null)
        .map(item => ({ label: `${item.advert?.title || ""} | ${item.advert?.id}`, value: item.advert!.id! }));
}

/** 复用 Vue imgContent 的活动远程选择接口。 */
export async function loadAdvertisementCampaignOptions(keyword: string): Promise<AdvertisementSelectorOption[]> {
    const response = await request.service.get<Array<{ code?: string | number; title?: string }>>(
        "/admin/campaign/selector",
        { params: { keyword } }
    );
    return (response.data || [])
        .filter(item => item.code != null)
        .map(item => ({ label: `${item.title || "--"} | ${item.code}`, value: item.code! }));
}

const initialValues: AdvertisementFormValues = {
    displayForm: 1,
    timeInterval: 1,
    beginTime: "",
    endTime: "",
    content: [{}]
};

function dateOnly(value?: string): string {
    return value?.slice(0, 10) || "";
}

/** 将日期控件值转换成 Vue 接口约定的起止时间。 */
function buildDateTime(value: string | undefined, endOfDay: boolean): string | undefined {
    const date = dateOnly(value);
    if (!date) return undefined;
    return `${date} ${endOfDay ? "23:59:59" : "00:00:00"}`;
}

/** 对齐 Vue ad/edit.vue：特定广告位不支持多图轮播。 */
export function isAdvertisementCarouselDisabled(positionType?: number, position?: number): boolean {
    if (positionType === 6) return [6, 7, 8].includes(position ?? 0);
    return position === 2;
}

/** 按 Vue imgContent 校验规则检查图片、跳转值和轮播排序。 */
function validateContent(values: AdvertisementFormValues): string[] {
    const errors: string[] = [];
    values.content.forEach(item => {
        if (!item.fileId) errors.push("请上传广告图片");
        if (item.linkType === 1 && !item.advertId) errors.push("请选择广告页");
        if (item.linkType === 2 && !item.link?.trim()) errors.push("请输入链接");
        if (item.linkType === 3 && !item.activityCode?.trim()) errors.push("请选择活动");
        if (
            values.displayForm === 2 &&
            (!Number.isInteger(item.sort) || Number(item.sort) < 1 || Number(item.sort) > 5)
        ) {
            errors.push("请填写权重排序");
        }
    });
    if (values.displayForm === 2) {
        const sorts = values.content.map(item => item.sort).filter((sort): sort is number => sort != null);
        if (new Set(sorts).size !== sorts.length) errors.push("填写的权重排序不支持重复，请修改");
    }
    return errors;
}

/** PC 广告位编辑基础表单，保留 Vue 的展示形式、周期和内容数组提交结构。 */
export function AdvertisementFormPage({
    id: propId,
    loadDetail = loadAdvertisementDetail,
    update = updateAdvertisement,
    loadAdvertOptions = loadAdvertisementAdvertOptions,
    loadCampaignOptions = loadAdvertisementCampaignOptions
}: AdvertisementFormPageProps) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const id = propId ?? Number(searchParams.get("id"));
    const [values, setValues] = useState<AdvertisementFormValues>(initialValues);
    const [loading, setLoading] = useState(id > 0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [uploadedImagePaths, setUploadedImagePaths] = useState<Record<number, string>>({});
    const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
    const [advertOptions, setAdvertOptions] = useState<Record<number, AdvertisementSelectorOption[]>>({});
    const [campaignOptions, setCampaignOptions] = useState<Record<number, AdvertisementSelectorOption[]>>({});
    const carouselDisabled = isAdvertisementCarouselDisabled(values.positionType, values.position);

    useEffect(() => {
        if (!(id > 0)) {
            setLoading(false);
            return;
        }
        let disposed = false;
        setLoading(true);
        void loadDetail(id)
            .then(detail => {
                if (disposed) return;
                setValues({
                    ...detail,
                    displayForm: Number(detail.displayForm) || 1,
                    timeInterval: Number(detail.timeInterval) || 1,
                    beginTime: dateOnly(detail.beginTime),
                    endTime: dateOnly(detail.endTime),
                    content: detail.content || []
                });
                setLoading(false);
            })
            .catch(() => {
                if (disposed) return;
                setErrorMessage("广告位详情加载失败");
                setLoading(false);
            });
        return () => {
            disposed = true;
        };
    }, [id, loadDetail]);

    const submit = async () => {
        if (values.displayForm !== 3 && (!values.beginTime || !values.endTime)) {
            setErrorMessage("请选择广告周期");
            return;
        }
        const contentErrors = validateContent(values);
        if (contentErrors.length) {
            setErrorMessage([...new Set(contentErrors)].join("；"));
            return;
        }
        setErrorMessage(null);
        const payload: AdvertisementFormPayload = {
            positionType: values.positionType,
            position: values.position,
            displayForm: values.displayForm,
            timeInterval: values.displayForm === 2 ? values.timeInterval : undefined,
            beginTime: values.displayForm === 3 ? undefined : buildDateTime(values.beginTime, false),
            endTime: values.displayForm === 3 ? undefined : buildDateTime(values.endTime, true),
            content: values.displayForm === 3 ? undefined : values.content
        };
        try {
            if (id > 0) await update(id, payload);
            message.success("广告位保存成功");
            // Vue 提交成功后返回上一页，避免用户重复提交当前编辑页。
            void navigate(-1);
        } catch {
            setErrorMessage("广告位保存失败");
            message.error("广告位保存失败");
        }
    };

    const addContent = () => {
        setValues(current =>
            current.content.length >= 5 ? current : { ...current, content: [...current.content, {}] }
        );
    };

    const removeContent = (index: number) => {
        setValues(current => {
            // Vue 至少保留一个图片内容模块，避免编辑页失去可提交的内容槽位。
            if (current.content.length <= 1) return current;
            return { ...current, content: current.content.filter((_, contentIndex) => contentIndex !== index) };
        });
        setUploadedImagePaths(current => {
            const next: Record<number, string> = {};
            Object.entries(current).forEach(([key, path]) => {
                const contentIndex = Number(key);
                if (contentIndex < index) next[contentIndex] = path;
                if (contentIndex > index) next[contentIndex - 1] = path;
            });
            return next;
        });
    };

    /** 对齐 Vue UploadImg：先校验图片类型和 5MB 大小，再回填上传结果。 */
    const uploadContentImage = async (index: number, { file, onError, onSuccess }: UploadRequestOptions) => {
        const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/avif"];
        if (!allowedTypes.includes(file.type)) {
            setErrorMessage("仅支持 JPG/PNG/GIF/WebP/AVIF 图片");
            onError(new Error("仅支持图片"));
            return;
        }
        if (file.size >= 5 * 1024 * 1024) {
            setErrorMessage("图片大小不能超过 5MB");
            onError(new Error("图片过大"));
            return;
        }

        setErrorMessage(null);
        setUploadingIndex(index);
        try {
            const uploadedFile = await uploadAdvertisementImage(file);
            if (!uploadedFile.id) throw new Error("上传响应缺少文件 ID");
            setValues(current => ({
                ...current,
                content: current.content.map((content, contentIndex) =>
                    contentIndex === index ? { ...content, fileId: uploadedFile.id } : content
                )
            }));
            setUploadedImagePaths(current => ({ ...current, [index]: uploadedFile.path || "" }));
            onSuccess(uploadedFile);
        } catch {
            setErrorMessage("广告图片上传失败");
            onError(new Error("广告图片上传失败"));
        } finally {
            setUploadingIndex(current => (current === index ? null : current));
        }
    };

    /** 单独维护各图片卡片的远程候选项，避免轮播卡片之间互相覆盖搜索结果。 */
    const searchContentOptions = async (
        index: number,
        keyword: string,
        loader: (keyword: string) => Promise<AdvertisementSelectorOption[]>,
        setter: Dispatch<SetStateAction<Record<number, AdvertisementSelectorOption[]>>>
    ) => {
        if (!keyword.trim()) {
            setter(current => ({ ...current, [index]: [] }));
            return;
        }
        try {
            const options = await loader(keyword);
            setter(current => ({ ...current, [index]: options }));
        } catch {
            setter(current => ({ ...current, [index]: [] }));
            setErrorMessage("跳转内容搜索失败");
        }
    };

    if (loading) return <div role="status">广告位加载中...</div>;

    return (
        <section aria-label="advertisement-form-page" className="flex h-full min-h-full flex-col">
            <Card className="mb-20">
                <Typography.Title level={4}>{id > 0 ? "修改广告位" : "新增广告位"}</Typography.Title>
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                {/* 对齐旧 Vue ad/edit.vue 的四列广告位表单布局。 */}
                <Form labelAlign="left">
                    <ZhGrid cols={4} gap={[20, 0]}>
                        <Form.Item label="展示形式" required>
                            <Radio.Group
                                aria-label="展示形式"
                                value={values.displayForm}
                                onChange={event =>
                                    setValues(current => ({
                                        ...current,
                                        displayForm: Number(event.target.value),
                                        timeInterval: 1,
                                        content: [{}]
                                    }))
                                }
                            >
                                {values.positionType === 11 ? <Radio value={3}>不展示</Radio> : null}
                                <Radio aria-label="展示形式-固定单图" value={1}>
                                    固定单图
                                </Radio>
                                <Radio aria-label="展示形式-多图轮播" disabled={carouselDisabled} value={2}>
                                    多图轮播
                                </Radio>
                            </Radio.Group>
                        </Form.Item>
                        {values.displayForm === 2 ? (
                            <Form.Item label="轮播间隔" required>
                                <Radio.Group
                                    aria-label="轮播间隔"
                                    options={[
                                        { label: "3秒", value: 1 },
                                        { label: "5秒", value: 2 }
                                    ]}
                                    value={values.timeInterval}
                                    onChange={event =>
                                        setValues(current => ({ ...current, timeInterval: Number(event.target.value) }))
                                    }
                                />
                            </Form.Item>
                        ) : null}
                    </ZhGrid>
                    <ZhGrid cols={4} gap={[20, 0]}>
                        {values.displayForm !== 3 ? (
                            <Form.Item label="广告周期" required>
                                <ZhDateRangePicker
                                    type="daterange"
                                    format="YYYY-MM-DD"
                                    valueFormat="YYYY-MM-DD"
                                    rangeSeparator="-"
                                    startPlaceholder="开始时间"
                                    endPlaceholder="结束时间"
                                    value={[dateOnly(values.beginTime), dateOnly(values.endTime)]}
                                    onChange={value =>
                                        setValues(current => ({
                                            ...current,
                                            beginTime: String(value?.[0] || ""),
                                            endTime: String(value?.[1] || "")
                                        }))
                                    }
                                />
                            </Form.Item>
                        ) : null}
                    </ZhGrid>
                </Form>
                <div className="grid grid-cols-2 gap-5">
                    {values.displayForm !== 3
                        ? values.content.map((item, index) => (
                              <Card
                                  className="relative bg-[#f7f8fa]"
                                  key={index}
                                  size="small"
                                  title={`图片内容 ${index + 1}`}
                              >
                                  <Space className="w-full" orientation="vertical">
                                      <Form.Item
                                          label="广告图片"
                                          labelCol={{ span: 24 }}
                                          required
                                          extra="支持jpg/png/webp/avif格式"
                                      >
                                          <ZhUpload
                                              accept="image/jpeg,image/png,image/gif,image/webp,image/avif"
                                              action=""
                                              aria-label={`上传广告图片 ${index + 1}`}
                                              disabled={uploadingIndex === index}
                                              httpRequest={options => void uploadContentImage(index, options)}
                                              limit={1}
                                              showFileList={false}
                                          >
                                              <div className="flex h-40 w-full cursor-pointer items-center justify-center rounded border border-dashed border-[#cbd8f2] bg-white">
                                                  {uploadedImagePaths[index] ? (
                                                      <img
                                                          alt={`广告图片 ${index + 1}`}
                                                          className="admin-upload-preview-media h-full w-full object-contain"
                                                          src={uploadedImagePaths[index]}
                                                      />
                                                  ) : (
                                                      <span>{item.fileId ? "替换图片" : "上传图片"}</span>
                                                  )}
                                              </div>
                                          </ZhUpload>
                                      </Form.Item>
                                      <Form.Item label="跳转类型">
                                          <Radio.Group
                                              aria-label={`跳转类型 ${index + 1}`}
                                              options={[
                                                  { label: "广告页", value: 1 },
                                                  { label: "链接", value: 2 },
                                                  ...([1, 6, 11].includes(values.positionType ?? 0)
                                                      ? [
                                                            { label: "活动", value: 3 },
                                                            { label: "不跳转", value: 4 }
                                                        ]
                                                      : [])
                                              ]}
                                              value={item.linkType ?? ""}
                                              onChange={event =>
                                                  setValues(current => ({
                                                      ...current,
                                                      content: current.content.map((content, contentIndex) =>
                                                          contentIndex === index
                                                              ? {
                                                                    ...content,
                                                                    linkType: Number(event.target.value) || undefined,
                                                                    // Vue 切换跳转类型时清空不再适用的旧值。
                                                                    advertId: undefined,
                                                                    link: undefined,
                                                                    activityCode: undefined
                                                                }
                                                              : content
                                                      )
                                                  }))
                                              }
                                          />
                                      </Form.Item>
                                      {item.linkType === 1 ? (
                                          <Select
                                              aria-label={`广告页 ${index + 1}`}
                                              filterOption={false}
                                              options={advertOptions[index] || []}
                                              placeholder="请搜索广告页标题或编号选择"
                                              showSearch
                                              value={item.advertId ?? ""}
                                              onChange={advertId =>
                                                  setValues(current => ({
                                                      ...current,
                                                      content: current.content.map((content, contentIndex) =>
                                                          contentIndex === index
                                                              ? { ...content, advertId: Number(advertId) || undefined }
                                                              : content
                                                      )
                                                  }))
                                              }
                                              onSearch={keyword =>
                                                  void searchContentOptions(
                                                      index,
                                                      keyword,
                                                      loadAdvertOptions,
                                                      setAdvertOptions
                                                  )
                                              }
                                          />
                                      ) : null}
                                      {item.linkType === 2 ? (
                                          <Input
                                              aria-label={`跳转链接 ${index + 1}`}
                                              maxLength={64}
                                              placeholder="请输入跳转链接"
                                              value={item.link || ""}
                                              onChange={event =>
                                                  setValues(current => ({
                                                      ...current,
                                                      content: current.content.map((content, contentIndex) =>
                                                          contentIndex === index
                                                              ? { ...content, link: event.target.value }
                                                              : content
                                                      )
                                                  }))
                                              }
                                          />
                                      ) : null}
                                      {item.linkType === 3 ? (
                                          <Select
                                              aria-label={`活动 ${index + 1}`}
                                              filterOption={false}
                                              options={campaignOptions[index] || []}
                                              placeholder="请搜索活动名称或活动id"
                                              showSearch
                                              value={item.activityCode || ""}
                                              onChange={activityCode =>
                                                  setValues(current => ({
                                                      ...current,
                                                      content: current.content.map((content, contentIndex) =>
                                                          contentIndex === index
                                                              ? { ...content, activityCode: String(activityCode) }
                                                              : content
                                                      )
                                                  }))
                                              }
                                              onSearch={keyword =>
                                                  void searchContentOptions(
                                                      index,
                                                      keyword,
                                                      loadCampaignOptions,
                                                      setCampaignOptions
                                                  )
                                              }
                                          />
                                      ) : null}
                                      {values.displayForm === 2 ? (
                                          <ZhInputNumber
                                              aria-label={`排序 ${index + 1}`}
                                              max={5}
                                              min={1}
                                              placeholder="请输入数字"
                                              precision={0}
                                              value={item.sort ?? ""}
                                              onChange={sort =>
                                                  setValues(current => ({
                                                      ...current,
                                                      content: current.content.map((content, contentIndex) =>
                                                          contentIndex === index
                                                              ? {
                                                                    ...content,
                                                                    sort: Number(sort) || undefined
                                                                }
                                                              : content
                                                      )
                                                  }))
                                              }
                                          />
                                      ) : null}
                                      {values.content.length > 1 ? (
                                          <Button
                                              className="absolute right-5 top-5"
                                              aria-label={`删除图片内容 ${index + 1}`}
                                              onClick={() => removeContent(index)}
                                          >
                                              删除
                                          </Button>
                                      ) : null}
                                  </Space>
                              </Card>
                          ))
                        : null}
                </div>
                {values.displayForm === 2 && values.content.length < 5 ? (
                    <Button aria-label="新增图片内容" onClick={addContent}>
                        新增
                    </Button>
                ) : null}
                <Typography.Text>已配置内容：{values.content.length} 条</Typography.Text>
            </Card>
            <Space className="operation-fixed-btn" size={0}>
                <Button aria-label="确定" type="primary" onClick={() => void submit()}>
                    确定
                </Button>
                <Button aria-label="返回" onClick={() => void navigate(-1)}>
                    返回
                </Button>
            </Space>
        </section>
    );
}

export default AdvertisementFormPage;
